import { useState, useCallback, useRef, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { toast } from 'sonner';
import { ONBOARDING_STEPS, mapStepDataToDb } from '../config/steps';

export function OnboardingFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleStepComplete = useCallback(async (data: any) => {
    if (!mounted.current || !user?.id) return;
    
    setLoading(true);
    try {
      const stepId = ONBOARDING_STEPS[currentStep].id;
      const mappedData = mapStepDataToDb(stepId, data);
      const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

      // Update user profile with step data
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          onboarding_progress: {
            currentStep: isLastStep ? 'completed' : ONBOARDING_STEPS[currentStep + 1].id,
            lastUpdated: new Date().toISOString(),
            isComplete: isLastStep,
            responses: {
              [stepId]: mappedData
            }
          }
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      if (mounted.current) {
        if (isLastStep) {
          // Add coins reward for completing onboarding
          await supabase
            .from('user_profiles')
            .update({
              career_coins: supabase.sql`career_coins + 100`,
              profile_completion: {
                percentage: 25,
                completed_sections: ['onboarding']
              }
            })
            .eq('user_id', user.id);

          navigate('/dashboard', { 
            replace: true,
            state: { 
              onboardingComplete: true,
              showCelebration: true,
              earnedCoins: 100
            }
          });
        } else {
          setCurrentStep(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Step completion error:', error);
      if (mounted.current) {
        toast.error('Failed to save progress');
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [currentStep, user?.id, navigate]);

  const CurrentStepComponent = ONBOARDING_STEPS[currentStep]?.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white">
            Step {currentStep + 1} of {ONBOARDING_STEPS.length}: {ONBOARDING_STEPS[currentStep].title}
          </h2>
          <div className="w-full bg-gray-700 h-2 mt-4 rounded-full">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        <Suspense fallback={<LoadingSpinner />}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-400">Saving your progress...</span>
            </div>
          ) : (
            <CurrentStepComponent
              onComplete={handleStepComplete}
              isLastStep={currentStep === ONBOARDING_STEPS.length - 1}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
}

export default OnboardingFlow;